import { useApi } from "@/api";
import { session } from "@/api/session";
import { ContentType, TPlatform } from "@/api/v1_0";
import { config } from "@/helpers";
import { RootStore } from "@/store/root";
import { makeAutoObservable } from "mobx";

export class AuthStore {
  private _rootStore: RootStore;

  public loading = false;
  public submitting = false;

  constructor(rootStore: RootStore) {
    this._rootStore = rootStore;

    makeAutoObservable(this);
  }

  public signUp = async (login: string, password: string): Promise<void> => {
    this._rootStore.logging.track("auth.signUp", { login });

    try {
      const api = useApi();

      const {
        data: { uuid: userUuid },
      } = await api.v10.appHttpApiV10ControllerAccountSignUp(
        {
          login,
          password,
        },
        {
          headers: {
            "Content-Type": ContentType.Json,
          },
        }
      );

      session.userUuid = userUuid;

      const {
        data: { token: accessToken, refreshToken },
      } = await api.login.appHttpApiSecurityControllerLogin(
        {
          login,
          password,
        },
        {
          headers: {
            "Content-Type": ContentType.Json,
          },
        }
      );

      session.accessToken = accessToken;
      session.refreshToken = refreshToken;

      const {
        data: { uuid: deviceUuid },
      } = await api.v10.appHttpApiV10ControllerAccountDevicesAttach(
        {
          deviceId: config.deviceUuid,
          platform: TPlatform.Web,
        },
        {
          headers: {
            "Content-Type": ContentType.Json,
          },
        }
      );

      session.deviceUuid = deviceUuid;

      this._rootStore.app.logged = true;
    } catch (err) {
      throw this._rootStore.logging.capture(err);
    }
  };

  public signIn = async (login: string, password: string): Promise<void> => {
    this._rootStore.logging.track("auth.signIn", { login });

    try {
      const api = useApi();

      const {
        data: { token: accessToken, refreshToken },
      } = await api.login.appHttpApiSecurityControllerLogin(
        {
          login,
          password,
        },
        {
          headers: {
            "Content-Type": ContentType.Json,
          },
        }
      );

      session.accessToken = accessToken;
      session.refreshToken = refreshToken;

      const {
        data: { uuid: deviceUuid },
      } = await api.v10.appHttpApiV10ControllerAccountDevicesAttach(
        {
          deviceId: config.deviceUuid,
          platform: TPlatform.Web,
        },
        {
          headers: {
            "Content-Type": ContentType.Json,
          },
        }
      );

      session.deviceUuid = deviceUuid;

      this._rootStore.app.logged = true;
    } catch (err) {
      throw this._rootStore.logging.capture(err);
    }
  };

  public signOut = async (): Promise<void> => {
    this._rootStore.logging.track("auth.signOut");

    session.clear();
    this._rootStore.profile.user = null;
    this._rootStore.app.logged = false;
  };

  public createSession = async (): Promise<string> => {
    this._rootStore.logging.track("auth.createSession");

    const api = useApi();

    const { data: result } =
      await api.session.appHttpApiSecurityControllerSessionCreate({
        headers: {
          "Content-Type": ContentType.Json,
        },
      });

    return result.uuid;
  };

  public attachSession = async (uuid: string): Promise<void> => {
    this._rootStore.logging.track("auth.attachSession");

    const api = useApi();

    await api.session.appHttpApiSecurityControllerSessionAttach(uuid, {
      headers: {
        "Content-Type": ContentType.Json,
      },
    });
  };

  public readSession = async (
    uuid: string
  ): Promise<{ token: string; refreshToken: string }> => {
    this._rootStore.logging.track("auth.readSession");

    const api = useApi();

    const { data: result } =
      await api.session.appHttpApiSecurityControllerSessionRead(uuid, {
        headers: {
          "Content-Type": ContentType.Json,
        },
      });

    return {
      token: result.token,
      refreshToken: result.refreshToken,
    };
  };

  public duplicateSession = async (
    accessToken: string,
    refreshToken: string
  ): Promise<void> => {
    this._rootStore.logging.track("auth.duplicateSession", {
      accessToken,
      refreshToken,
    });

    try {
      const api = useApi();

      session.accessToken = accessToken;
      session.refreshToken = refreshToken;

      const {
        data: { uuid: deviceUuid },
      } = await api.v10.appHttpApiV10ControllerAccountDevicesAttach(
        {
          deviceId: config.deviceUuid,
          platform: TPlatform.Web,
        },
        {
          headers: {
            "Content-Type": ContentType.Json,
          },
        }
      );

      session.deviceUuid = deviceUuid;

      this._rootStore.app.logged = true;
    } catch (err) {
      throw this._rootStore.logging.capture(err);
    }
  };
}
